<template>
    <div
        class="address"
        :class="{ 'address--short': onlyShowZipCode }"
    >
        <div class="col-12 address__input-container address__input-container--zip-code">
            <div class="address__input-container--zip-code-inner">
                <CieloInputV2
                    name="ZipCode"
                    :label="$ml.get('zipcode')"
                    v-model="syncedZipCode"
                    v-validate="zipCodeValidation"
                    :data-vv-as="$ml.get('zipcode')"
                    :type="nationalityZipCodeTypeValidate"
                    :error="errors.first('ZipCode')"
                    :mask="zipCodeMask"
                    :disabled="disableZipCode"
                    :maxlength="selectedCountry === 'BR' ? 9 : 10"
                />

                <a
                    v-show="isNational && !disableZipCode"
                    href="http://www.buscacep.correios.com.br/sistemas/buscacep/buscaCepEndereco.cfm"
                    target="_blank"
                    rel="noopener noreferrer"
                >{{ this.$ml.get("forgotIt") }}</a>
            </div>
        </div>

        <div
            v-show="!onlyShowZipCode || !isNational"
            class="address__inputs-container"
        >
            <div class="col-12 address__input-container">
                <CieloInputV2
                    name="Street"
                    :label="$ml.get('street')"
                    v-model="syncedStreet"
                    v-validate="{ required: true, max: 256 }"
                    :data-vv-as="$ml.get('street')"
                    type="text"
                    :error="errors.first('Street')"
                    :disabled="disableStreet"
                />
            </div>

            <div class="col-12 address__input-container">
                <CieloInputV2
                    v-show="selectedCountry === 'CA' || selectedCountry === 'US'"
                    name="AddressLine2"
                    :label="$ml.get('addressLine2')"
                    v-model="syncedAddressLine2"
                    v-validate="'max:256'"
                    type="text"
                    :data-vv-as="$ml.get('addressLine2')"
                    :error="errors.first('AddressLine2')"
                    :disabled="disableAddressLine2"
                />
            </div>

            <div class="col-6 col-lg-6 address__input-container">
                <CieloInputV2
                    v-show="isNational"
                    name="Number"
                    :label="$ml.get('number')"
                    v-model="syncedNumber"
                    v-validate="{ required: isNational, max: 8 }"
                    :data-vv-as="$ml.get('number')"
                    type="text"
                    :error="errors.first('Number')"
                    :disabled="disableNumber"
                />
            </div>

            <div class="col-6 col-lg-6 address__input-container">
                <CieloInputV2
                    v-show="isNational"
                    name="Complement"
                    :label="$ml.get('complement')"
                    v-model="syncedComplement"
                    v-validate="'max:14'"
                    :data-vv-as="$ml.get('complement')"
                    type="text"
                    :error="errors.first('Complement')"
                    :disabled="disableComplement"
                />
            </div>

            <div class="col-12 address__input-container">
                <CieloInputV2
                    v-show="isNational"
                    name="District"
                    :label="$ml.get('district')"
                    v-model="syncedDistrict"
                    v-validate="{ required: isNational, max: 64 }"
                    :data-vv-as="$ml.get('district')"
                    type="text"
                    :error="errors.first('District')"
                    :disabled="disableDistrict"
                />
            </div>

            <div class="col-12 col-lg-6 address__input-container">
                <CieloInputV2
                    name="City"
                    :label="$ml.get('city')"
                    v-model="syncedCity"
                    v-validate="'required|max:64'"
                    :data-vv-as="$ml.get('city')"
                    type="text"
                    :error="errors.first('City')"
                    :disabled="disableCityAndState"
                />
            </div>

            <div class="col-12 col-lg-6 address__input-container">
                <CieloInputV2
                    v-show="!isNational"
                    name="State"
                    :label="$ml.get('foreigner')"
                    v-model="syncedState"
                    v-validate="'required|max:2'"
                    :data-vv-as="$ml.get('state')"
                    type="text"
                    :error="errors.first('State')"
                />

                <CieloSelectV2
                    v-show="isNational"
                    @change="selectState"
                    :title="$ml.get('chooseYourState')"
                    :label="$ml.get('state')"
                    :option-items="countryStates[selectedCountry]"
                    :selected-item.sync="syncedState"
                    :disabled="disableCityAndState"
                />
            </div>
        </div>
    </div>
</template>

<script>
	import Swal from 'sweetalert2';

	import loader from '@/helpers/loader';
	import { isNullOrWhitespace } from '@/helpers/stringHelper';
	
	import  { StatesMap } from '@/models/AddressStates';
	
	import { FetchPostalCodeAsync } from '@/services/PostalCodeFetcher';

	const CieloInputV2 = () =>
		import(
			/* webpackChunkName: "cielo-input-v2" */
			'@/components/ClickToPay/template/CieloInputV2'
		);
	import CieloSelectV2 from '@/components/ClickToPay/template/CieloSelectV2/CieloSelectV2';
	

	export default {

		components: {
			CieloInputV2,
			CieloSelectV2,
		},

		mounted() {
			loader.multiLanguage = this.$ml;
			this.$validator.validate();
			this.$emit('update:addressErrors', this.errors);
			if(this.zipCode) {
				this.onlyShowZipCode = false;
			}
		},

		data() {
			return {
				disableCityAndState: false,
				selectedCountry: 'BR',
				countryStates: {},
				onlyShowZipCode: this.showShortForm,
				excludeFocusInputsName: ['AddressLine2'],
			};
		},

		computed: {
			syncedZipCode: {
				get: (self) => self.zipCode,
				set(value) {
					this.$emit('update:zipCode', value);
				},
			},
			syncedStreet: {
				get: (self) => self.street,
				set(value) {
					this.$emit('update:street', value);
				},
			},
			syncedAddressLine2: {
				get: (self) => self.addressLine2,
				set(value) {
					this.$emit('update:addressLine2', value);
				},
			},
			syncedNumber: {
				get: (self) => self.number,
				set(value) {
					this.$emit('update:number', value);
				},
			},
			syncedComplement: {
				get: (self) => self.complement,
				set(value) {
					this.$emit('update:complement', value);
				},
			},
			syncedDistrict: {
				get: (self) => self.district,
				set(value) {
					this.$emit('update:district', value);
				},
			},
			syncedCity: {
				get: (self) => self.city,
				set(value) {
					this.$emit('update:city', value);
				},
			},
			syncedState: {
				get: (self) => self.state,
				set(value) {
					this.$emit('update:state', value);
				},
			},
			nationalityZipCodeTypeValidate() {
				return 'text';
			},
			zipCodeMask() {
				if (!this.isNational) {
					return '';
				};
				return this.selectedCountry === 'BR'
					? ['#####-###']
					: this.selectedCountry === 'US'
						? ['##########']
						: this.selectedCountry === 'CA'
							? ['A#A #A#']
							: '';
			},
			zipCodeValidation() {
				if (!this.isNational) {
					return 'required';
				} else {
					return 'required|min:8';
				};
			},
			addressTypeGetter() {
				return `${this.addressType}/getIsNational`;
			},
			isNational() {
				return this.$store.getters[this.addressTypeGetter];
			},
		},

		watch: {
			zipCode(value) {
				if (
					value &&
					value.length >= 8 &&
					this.isNational
				) {
					this.fetchPostalCodeData(value);
					this.disableCityAndState = true; // Desabilita os campos de cidade e estado
					this.resetAddressFields();
					this.onlyShowZipCode = false;
				} else if (!this.isNational) {
					this.disableCityAndState = false; // Habilita os campos de cidade e estado
					this.onlyShowZipCode = false;
				}
			},
		},

		props: {
			zipCode: {
				type: String,
				default: '',
			},
			street: {
				type: String,
				default: '',
			},
			addressLine2: {
				type: String,
				default: '',
			},
			number: {
				type: String,
				default: '',
			},
			complement: {
				type: String,
				default: '',
			},
			district: {
				type: String,
				default: '',
			},
			city: {
				type: String,
				default: '',
			},
			state: {
				type: String,
				default: '',
			},
			addressErrors: {
				type: Object,
				default() {
					return {};
				},
			},
			disableStreet: {
				type: Boolean,
				default: false,
			},
			disableAddressLine2: {
				type: Boolean,
				default: false,
			},
			disableDistrict: {
				type: Boolean,
				default: false,
			},
			disableCity: {
				type: Boolean,
				default: false,
			},
			disableState: {
				type: Boolean,
				default: false,
			},
			disableNumber: {
				type: Boolean,
				default: false,
			},
			disableComplement: {
				type: Boolean,
				default: false,
			},
			disableZipCode: {
				type: Boolean,
				default: false,
			},
			showShortForm: {
				type: Boolean,
				default: false,
			},
			addressType: {
				type: String,
				default: 'billingAddress',
				validator: value => ['billingAddress', 'shippingAddress'].includes(value)
			},
		},

		created() {
			this.countryStates = this.getCountryStates();
		},

		methods: {

			getCountryStates() {
				let countryStates = {};
				for (let country in StatesMap) {
					countryStates[country] = Object.entries(StatesMap[country]).map(([Value, Text]) => ({ Text, Value }));
				}
				return countryStates;
			},

			selectState(item) {
				this.syncedState = item;
			},
			resetAddressFields() {
				this.syncedStreet = null;
				this.syncedAddressLine2 = null;
				this.syncedNumber = null;
				this.syncedComplement = null;
				this.syncedDistrict = null;
				this.syncedCity = null;
				this.syncedState = null;
			},
			async fetchPostalCodeData(postalCode) {
				loader.Start(this.$ml.get('addressSearchZipcode'));

				FetchPostalCodeAsync(postalCode)
					.then((response) =>
						this.buildPostalCodeEventContract(response.data, postalCode)
					)
					.then((contract) => this.$emit('postalCodeFetched', contract))
					.then(() => {
						loader.Stop();
						setTimeout(() => this.focusFirstEmpty(), 1000);
						this.$validator.validate();
					})
					.catch((error) => {
						this.$emit('postalCodeFetched', {});
						this.disableCityAndState = false; // Habilita os campos de cidade e estado
						Swal.fire({
							type: 'error',
							text: this.$ml.get('addressZipcodeNotFound'),
							confirmButtonText: this.$ml.get('addressUnderstood'),
							showCancelButton: false,
							showLoaderOnConfirm: true,
							allowOutsideClick: false,
							allowEscapeKey: false,
						});
					});
			},
			buildPostalCodeEventContract(data, postalCode) {
				if (!data) return {};

				const fields = {
					Street: data.street,
					addressLine2: data.addressLine2 || null,
					Number: data.number || null,
					Complement: data.complement || null,
					District: data.district,
					City: data.city,
					State: data.state,
					ZipCode: postalCode,
				};

				const fetchedFields = [];

				if (isNullOrWhitespace(fields.Street) == false)
					fetchedFields.push('street');
				if (isNullOrWhitespace(fields.District) == false)
					fetchedFields.push('district');
				if (isNullOrWhitespace(fields.City) == false)
					fetchedFields.push('city');
				if (isNullOrWhitespace(fields.State) == false)
					fetchedFields.push('state');

				return {
					fetchedFields,
					fields,
				};
			},

			focusFirstEmpty() {
				const inputs = this.$el.querySelectorAll('input');
				const emptyInput = Array.from(inputs).find((input) =>
					input.value.trim() === '' && !this.excludeFocusInputsName.includes(input.name)
				);

				if(emptyInput) {
					emptyInput.focus();
				}
			},

			validateAddress() {
				this.$validator.validate();
				this.$emit('update:addressErrors', this.errors);
			},
		},
	};
</script>

<style lang="scss" scoped>
	.address {
		margin-left: -10px;
		margin-right: -10px;
		flex: 1 0 auto;

		&__inputs-container {
			display: flex;
			flex-wrap: wrap;
		}

		&__input-container {
			padding-left: 10px;
			padding-right: 10px;
		}

		&__input-container--zip-code {
			position: relative;

			a {
				position: absolute;
				top: 15px;
				right: 22px;
				font-size: 14px;
				color: #0774e7;
				text-decoration: none;
			}

			&-inner {
				position: relative;
			}
		}
		.selectCountry {
			margin-bottom: 20px;
		}
	}
</style>
